import * as React from "react";
import { observer } from "mobx-react";
import { Dates } from "src/components/shared/Dates/Dates";
import {
  UitkLayoutGrid,
  UitkLayoutGridItem,
  UitkLayoutConditionalGridSpan,
  UitkLayoutConditionalGridColumnRow,
} from "uitk-react-layout-grid";
import { Location } from "src/components/shared/Location/Location";
import { Travelers } from "src/components/shared/Travelers/Travelers";
import { TimeDropdown } from "src/components/shared/TimeDropdown/TimeDropdown";
import { ToAirportViewProps } from "../../typings";
import { locKeys } from "../l10n";
import { GTHiddenSearchData } from "./GTHiddenSearchData";
import { Viewport, ViewSmall, ViewMedium } from "@shared-ui/viewport-context";
import { DirectionLabel } from "./DirectionLabel";
import { LobErrorSummary } from "src/components/shared/LobErrorSummary/LobErrorSummary";

export const ToAirportView: React.FunctionComponent<ToAirportViewProps> = observer((props) => {
  const {
    carWizardState,
    carWizardState: { pickupTime, isToAirportFormValid, updateInvalidFormsState },
    setInputsRefs,
  } = props;
  const { gtHeaderTo, gtHeaderFrom, gtArrivalTime } = locKeys;
  const format12hrConfig = carWizardState.config.timeFormats.timeFormat12Hour;

  const mainGridLayout: UitkLayoutConditionalGridColumnRow = { medium: 12 };
  const halfGridColumn: UitkLayoutConditionalGridSpan = { medium: 6 };

  const subheadingColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 6 };

  const locationColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 12 };

  const travelersColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 12 };
  const datesColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 6 };
  const timeDropdownColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 6 };
  const errorSummaryGridColumns: UitkLayoutConditionalGridSpan = { small: 2, medium: 12 };

  if (isToAirportFormValid) {
    updateInvalidFormsState();
  }

  return (
    <>
      {!isToAirportFormValid && (
        <LobErrorSummary
          locHeadingToken={props.getLocError(false)!}
          locHeadingArgs={carWizardState.moreThanOneError() && [carWizardState.numberOfErrors]}
          locLinkToken={props.getLocError(true)}
          linkClickFocusId="id-roundtrip-error"
          inputErrorRef={carWizardState.errorInputRef}
          colSpan={errorSummaryGridColumns}
        />
      )}
      <Viewport>
        <ViewSmall>
          <>
            <DirectionLabel colSpan={subheadingColSpan} locKey={gtHeaderFrom} />
            <Location
              setInputsRefs={setInputsRefs}
              originField={true}
              destinationField={false}
              lobState={carWizardState}
              colSpan={locationColSpan}
            />
            <Travelers lobState={carWizardState} colSpan={travelersColSpan} />

            <DirectionLabel colSpan={subheadingColSpan} locKey={gtHeaderTo} />

            <Location
              setInputsRefs={setInputsRefs}
              originField={false}
              destinationField={true}
              lobState={carWizardState}
              colSpan={locationColSpan}
            />

            <Dates lobState={carWizardState} singleDate={true} colSpan={datesColSpan} />
            <TimeDropdown
              labelKey={gtArrivalTime}
              timeValues={carWizardState.config.gtConfig.timeValues}
              onChange={carWizardState.setPickupTime}
              value={pickupTime}
              invalidKey={carWizardState.timeStartInvalidKey}
              colSpan={timeDropdownColSpan}
              use12hr={format12hrConfig}
            />
          </>
        </ViewSmall>
        <ViewMedium>
          <>
            <UitkLayoutGridItem colSpan={halfGridColumn}>
              <UitkLayoutGrid columns={mainGridLayout} space="three">
                <DirectionLabel colSpan={subheadingColSpan} locKey={gtHeaderFrom} />
                <Location
                  setInputsRefs={setInputsRefs}
                  originField={true}
                  destinationField={false}
                  lobState={carWizardState}
                  colSpan={locationColSpan}
                />
                <Travelers lobState={carWizardState} colSpan={travelersColSpan} />
              </UitkLayoutGrid>
            </UitkLayoutGridItem>

            <UitkLayoutGridItem colSpan={halfGridColumn}>
              <UitkLayoutGrid columns={mainGridLayout} space="three">
                <DirectionLabel colSpan={subheadingColSpan} locKey={gtHeaderTo} />
                <Location
                  setInputsRefs={setInputsRefs}
                  originField={false}
                  destinationField={true}
                  lobState={carWizardState}
                  colSpan={locationColSpan}
                />
                <Dates lobState={carWizardState} singleDate={true} colSpan={datesColSpan} />
                <TimeDropdown
                  labelKey={gtArrivalTime}
                  timeValues={carWizardState.config.gtConfig.timeValues}
                  onChange={carWizardState.setPickupTime}
                  value={pickupTime}
                  invalidKey={carWizardState.timeStartInvalidKey}
                  colSpan={timeDropdownColSpan}
                  use12hr={format12hrConfig}
                />
              </UitkLayoutGrid>
            </UitkLayoutGridItem>
          </>
        </ViewMedium>
      </Viewport>
      <GTHiddenSearchData pickUpTime={pickupTime} />
    </>
  );
});
